<template>
  <transition name="bubble-fade">
    <section
      v-if="visible && resourceId"
      ref="bubble"
      class="block-bubble"
      :class="{ 'is-pinned': pinned, 'is-placed-left': bubblePos.placeLeft }"
      role="dialog"
      :aria-label="'时间块评论：' + (title || '时间块')"
      :style="{ left: bubblePos.left + 'px', top: bubblePos.top + 'px', width: bubblePos.width + 'px' }"
      @mouseenter="$emit('pointer-enter')"
      @mouseleave="$emit('pointer-leave')"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    >
      <!-- 头部：色条 + 当前时间块标题/时间 + 点赞 + 关闭 -->
      <div class="bubble-head">
        <span class="head-dot" :style="{ background: dotColor }"></span>
        <div class="head-text">
          <div class="head-context">{{ headContext }}</div>
          <div class="head-title" :title="title">{{ title || '时间块' }}</div>
          <div v-if="subtitle" class="head-sub">{{ subtitle }}</div>
        </div>
        <button
          class="icon-btn like-btn"
          :class="{ liked }"
          type="button"
          :title="liked ? '取消点赞' : '点赞'"
          @click="toggleLike"
        >
          <FaIcon :icon="liked ? faHeart : farHeart" :size="15" />
          <span v-if="likeCount > 0" class="like-count">{{ likeCount }}</span>
        </button>
        <button class="icon-btn close-btn" type="button" title="关闭评论气泡，再选择其他时间块" @click="$emit('close')">
          <FaIcon :icon="faXmark" :size="12" />
        </button>
      </div>

      <!-- 评论列表（楼中楼：顶层评论 + 其下回复缩进） -->
      <div class="bubble-body">
        <div v-if="loading" class="tip">加载中…</div>
        <div v-else-if="!rows.length" class="tip empty">
          <FaIcon :icon="farComment" :size="18" />
          <span>还没有评论，来说点什么</span>
        </div>
        <div
          v-for="row in rows"
          :key="row.c.id"
          class="comment-item"
          :class="{ 'is-reply': row.depth > 0 }"
        >
          <UserAvatar class="comment-avatar" :url="row.c.avatarUrl" :name="row.c.nickname" />
          <div class="comment-body">
            <div class="comment-meta">
              <span class="comment-nick">{{ row.c.nickname || '匿名用户' }}</span>
              <span class="comment-time">{{ formatTime(row.c.createdAt) }}</span>
            </div>
            <div class="comment-text"><span v-if="row.c.replyToNickname" class="reply-to">回复 @{{ row.c.replyToNickname }}：</span>{{ row.c.content }}</div>
          </div>
          <button
            v-if="canReply(row.c)"
            class="icon-btn reply-btn"
            type="button"
            :title="row.depth > 0 ? '回复这条回复' : '回复这条评论'"
            @click="replyTo(row.c)"
          >
            <FaIcon :icon="faReply" :size="11" />
          </button>
          <button
            v-if="row.c.canDelete"
            class="icon-btn del-btn"
            type="button"
            title="删除这条评论"
            @click="removeComment(row.c)"
          >
            <FaIcon :icon="faTrashCan" :size="11" />
          </button>
        </div>
      </div>

      <!-- 发评论 / 回复 -->
      <div class="bubble-foot">
        <div v-if="replyTarget" class="reply-target">
          <span class="reply-target-text">回复 @{{ replyTarget.nickname || '好友' }}</span>
          <button class="icon-btn reply-cancel" type="button" title="取消回复" @click="cancelReply">
            <FaIcon :icon="faXmark" :size="10" />
          </button>
        </div>
        <div class="foot-row">
          <input
            ref="commentInput"
            v-model="content"
            class="comment-input"
            type="text"
            maxlength="100"
            :placeholder="replyTarget ? '回复 @' + (replyTarget.nickname || '好友') + '…' : '写条评论…'"
            @keyup.enter="submit"
          >
          <button
            class="send-btn"
            type="button"
            :disabled="!canSend || sending"
            title="发送"
            @click="submit"
          >
            <FaIcon :icon="faPaperPlane" :size="12" />
          </button>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
import FaIcon from '@/components/common/FaIcon.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { faHeart } from '@fortawesome/free-solid-svg-icons/faHeart'
// regular 包里导出名仍是 faXxx，前缀体现在图标定义里，所以这里做别名
import { faHeart as farHeart } from '@fortawesome/free-regular-svg-icons/faHeart'
import { faComment as farComment } from '@fortawesome/free-regular-svg-icons/faComment'
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons/faPaperPlane'
import { faReply } from '@fortawesome/free-solid-svg-icons/faReply'
import { listComments, createComment, deleteComment, like, unlike, getLikeStatus } from '@/api/comment.js'
import { toNumber } from '@/utils/number.js'
import { notifySuccess, notifyError } from '@/utils/notify.js'
import { portalToFullscreen } from '@/utils/fullscreenPortal'
import { runAchievementCheck } from '@/components/achievement/achievementStore.js'

export default {
  name: 'BlockBubble',
  components: { FaIcon, UserAvatar },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 被评论的时间块，{ id, text, timeLabel, color } */
    block: {
      type: Object,
      default: null
    },
    resourceType: {
      type: String,
      default: 'TIMEBLOCK'
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    /**
     * 源时间块的屏幕坐标 { top,left,right,bottom,width,height }（viewport 坐标系）。
     * 气泡据此贴在时间块右侧；右边放不下时自动翻到左侧。传 null 则退化成视口居中。
     */
    anchor: {
      type: Object,
      default: null
    },
    /** 是否已固定（点击时间块 / 鼠标移入气泡）：固定后不再自动消失。 */
    pinned: {
      type: Boolean,
      default: false
    },
    /**
     * 是否支持「点击时间块固定气泡」。
     * 好友页（只读）为 true；所有者自己的表格点击时间块是「修改记录」，不能固定气泡，传 false。
     * 只影响顶部提示文案，不影响其它行为。
     */
    pinnable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      comments: [],
      content: '',
      /** 当前回复目标评论（null = 发顶层评论）。回复「回复」时后端会归一化到同一顶层评论。 */
      replyTarget: null,
      loading: false,
      sending: false,
      liked: false,
      likeCount: 0,
      // 实测气泡高度：只有在拿到真实高度后，纵向才能正确收进视口
      bubbleH: 0,
      // 请求序号：丢弃过期响应，避免评论串台
      loadToken: 0,
      faHeart,
      farHeart,
      farComment,
      faXmark,
      faTrashCan,
      faPaperPlane,
      faReply
    }
  },
  computed: {
    resourceId() {
      return this.block ? this.block.id : null
    },
    /** 顶部提示文案：固定态 / 可固定（好友页）/ 仅悬停（所有者自己的表格） */
    headContext() {
      if (this.pinned) return '正在评论此时间块'
      return this.pinnable ? '悬停预览 · 点击时间块可固定' : '悬停查看评论 · 移入气泡即可回复'
    },
    /** 当前登录用户 ID（用于判断某条评论是不是自己发的），读 localStorage 的 user.id */
    currentUserId() {
      try {
        const raw = localStorage.getItem('user')
        if (!raw) return null
        const u = JSON.parse(raw)
        return u && u.id != null ? String(u.id) : null
      } catch (e) {
        return null
      }
    },
    /**
     * 把后端返回的平铺评论组装成「楼中楼」两层展示行：
     * 顶层评论（沿用后端倒序）+ 其下回复（按时间正序）。
     * 父评论已被删的孤儿回复会被提升为顶层行兜底，避免评论凭空消失。
     */
    rows() {
      const list = this.comments || []
      const tops = []
      const topIds = {}
      list.forEach(c => {
        if (!c.parentId) {
          tops.push(c)
          topIds[String(c.id)] = true
        }
      })
      const repliesByParent = {}
      list.forEach(c => {
        if (!c.parentId) return
        const key = String(c.parentId)
        if (!topIds[key]) return
        if (!repliesByParent[key]) repliesByParent[key] = []
        repliesByParent[key].push(c)
      })
      const rows = []
      const placed = {}
      tops.forEach(t => {
        rows.push({ c: t, depth: 0 })
        placed[String(t.id)] = true
        const reps = (repliesByParent[String(t.id)] || [])
          .slice()
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        reps.forEach(r => {
          rows.push({ c: r, depth: 1 })
          placed[String(r.id)] = true
        })
      })
      list.forEach(c => {
        if (!placed[String(c.id)]) rows.push({ c, depth: c.parentId ? 1 : 0 })
      })
      return rows
    },
    dotColor() {
      return (this.block && this.block.color) || '#4aa46a'
    },
    canSend() {
      return this.content.trim().length > 0
    },
    /**
     * 气泡落点：优先贴在时间块**右侧**（与块顶对齐），右边放不下就翻到左侧，
     * 左右都放不下时取空间更大的一侧并收进视口；没有 anchor 时退回视口居中。
     */
    bubblePos() {
      const vw = window.innerWidth || 0
      const vh = window.innerHeight || 0
      const width = Math.max(240, Math.min(320, vw - 24))
      const gap = 10
      const a = this.anchor
      if (!a) {
        const h = this.bubbleH || 320
        return {
          left: Math.max(8, (vw - width) / 2),
          top: Math.max(8, Math.min((vh - h) / 2, vh - h - 8)),
          width,
          placeLeft: false
        }
      }
      let placeLeft = false
      let left = a.right + gap
      if (left + width > vw - 8) {
        const leftSide = a.left - gap - width
        // 左侧塞得下就翻到左边；两侧都塞不下时选空间更大的一侧，再由下面的钳制收进视口
        if (leftSide >= 8) {
          left = leftSide
          placeLeft = true
        } else if (a.left > vw - a.right) {
          left = leftSide
          placeLeft = true
        }
      }
      left = Math.max(8, Math.min(left, Math.max(8, vw - width - 8)))
      // 纵向：与块顶对齐，再整体钳进视口（高度未知时先按 320 估算，测到真实高度后会再校正一次）
      const h = this.bubbleH || 320
      let top = a.top
      if (top + h > vh - 8) top = Math.max(8, vh - h - 8)
      if (top < 8) top = 8
      return { left, top, width, placeLeft }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.content = ''
        this.replyTarget = null
        this.load()
        this.$nextTick(() => {
          // 全屏视图：气泡是 fixed 浮层，但全屏中的 .tablebox 在浏览器 top layer，
          // 不搬进去会被全屏视图整块盖住（原理见 @/utils/fullscreenPortal.js）
          portalToFullscreen(this.$refs.bubble || this.$el)
          this.reposition()
        })
      } else {
        this.replyTarget = null
      }
    },
    anchor() {
      this.$nextTick(this.reposition)
    },
    // 评论条数变化会改变气泡高度，需要重新校正落点与小箭头位置
    comments() {
      this.$nextTick(this.reposition)
    },
    loading() {
      this.$nextTick(this.reposition)
    },
    // 固定态下点空白处关闭（没有遮罩了，只能靠文档级监听兜底）
    pinned(val) {
      if (val) {
        document.addEventListener('mousedown', this.onDocMouseDown, true)
      } else {
        document.removeEventListener('mousedown', this.onDocMouseDown, true)
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocMouseDown, true)
  },
  methods: {
    /** 量出真实高度 → 触发落点重算 → 再定位指回时间块的小箭头。 */
    reposition() {
      const el = this.$refs.bubble
      if (!el) return
      const h = el.offsetHeight
      if (h && h !== this.bubbleH) {
        this.bubbleH = h
        // 落点依赖 bubbleH，等这一轮 DOM 更新后再摆箭头
        this.$nextTick(this.updateArrow)
        return
      }
      this.updateArrow()
    },
    updateArrow() {
      const el = this.$refs.bubble
      if (!el) return
      const a = this.anchor
      if (!a) {
        el.style.setProperty('--bubble-arrow', '-999px')
        return
      }
      const top = parseFloat(el.style.top) || 0
      const h = this.bubbleH || el.offsetHeight || 0
      // 箭头对齐时间块的垂直中心，并留出 14px 不贴边
      const center = a.top + a.height / 2 - top
      const clamped = Math.min(Math.max(center, 14), Math.max(14, h - 14))
      el.style.setProperty('--bubble-arrow', clamped + 'px')
    },
    onDocMouseDown(e) {
      const el = this.$refs.bubble
      if (el && el.contains(e.target)) return
      this.$emit('close')
    },
    /**
     * 焦点进入气泡（典型：点进评论输入框）→ 告诉父组件「用户开始写了，别再自动收起」。
     *
     * <p>⚠️ **这个坑必须堵**：气泡平时是「悬停预览」态（ListPage 与好友页 EDIT 态都不会固定），
     *    鼠标一移出气泡就走 300ms 宽限收起。而用户点进输入框后往往要挪开鼠标去够键盘
     *    （或者手抖一下），气泡当场被销毁 → **焦点掉回 body** →
     *    再按空格就成了浏览器的「空格翻页」，表现为「气泡里打不了字，一按空格就退出气泡并且页面下滑」。
     *    修复办法是让父组件在收到本事件后把气泡升级成固定态。</p>
     */
    onFocusIn() {
      this.$emit('interacting', true)
    },
    /**
     * 焦点离开气泡。
     *
     * ⚠️ 用 `relatedTarget` 判断是不是真的离开：焦点只是在气泡内部挪
     *    （输入框 → 点赞/关闭按钮）也会触发 focusout，那不能算离开。
     */
    onFocusOut(e) {
      const el = this.$refs.bubble
      if (el && e && e.relatedTarget && el.contains(e.relatedTarget)) return
      this.$emit('interacting', false)
    },
    async load() {
      if (!this.resourceId) return
      const token = ++this.loadToken
      this.loading = true
      try {
        const [commentRes, likeRes] = await Promise.all([
          listComments(this.resourceType, this.resourceId),
          getLikeStatus(this.resourceType, this.resourceId)
        ])
        if (token !== this.loadToken) return
        this.comments = (commentRes.success && commentRes.data) ? commentRes.data : []
        if (likeRes.success && likeRes.data) {
          this.liked = Boolean(likeRes.data.liked)
          // 后端 Long 会被序列化成字符串（见 utils/number.js），必须转成数字，
          // 否则下面 toggleLike 里的 +1 会变成字符串拼接，角标显示成 "01"
          this.likeCount = toNumber(likeRes.data.count)
        } else {
          this.liked = false
          this.likeCount = 0
        }
      } catch (e) {
        if (token !== this.loadToken) return
        console.error('[BlockBubble] 加载评论失败', e)
        this.comments = []
      } finally {
        if (token === this.loadToken) this.loading = false
      }
    },
    async submit() {
      const text = this.content.trim()
      if (!text || this.sending) return
      this.sending = true
      try {
        const payload = {
          resourceType: this.resourceType,
          resourceId: this.resourceId,
          content: text
        }
        // 回复：带**被回复的那条评论**的 id，不要自己归一化到顶层。
        // 归一化（parent_id 收敛到顶层）+ 认定被回复者（reply_to_user_id）都由后端做：
        // 后端拿这条评论的 author 当 reply_to_user_id，才是「A 回复 B」而不是「回复楼主」。
        // ⚠️ 曾经写成 `replyTarget.parentId || replyTarget.id`，回复「回复」时会把顶层评论 id 发过去，
        //    导致被回复者永远是楼主（用户反馈过）。
        if (this.replyTarget) {
          payload.parentId = this.replyTarget.id
        }
        const res = await createComment(payload)
        if (!res.success || res.code !== 200) {
          notifyError('发送失败', res.message || '评论未能发送，请稍后重试')
          return
        }
        const isReply = Boolean(this.replyTarget)
        this.content = ''
        this.replyTarget = null
        // 右上角轻提示：明确告诉用户这条到底发出去没有（自己的块 / 好友的块同一个气泡）
        notifySuccess(isReply ? '回复已发送' : '评论已发送', isReply ? '好友会收到提醒' : '已保存到该时间块的评论区')
        await this.load()
        this.$emit('changed')
        // 评论可能达成「第一次互动 / 互动次数」类成就
        runAchievementCheck(true)
      } catch (e) {
        notifyError('发送失败', '网络异常，评论未能发送，请稍后重试')
      } finally {
        this.sending = false
      }
    },
    async removeComment(comment) {
      if (!window.confirm('确定删除这条评论吗？')) return
      try {
        const res = await deleteComment(comment.id)
        if (!res.success || res.code !== 200) {
          notifyError('删除失败', res.message || '这条评论未能删除，请稍后重试')
          return
        }
        notifySuccess('已删除', '评论已删除')
        await this.load()
        this.$emit('changed')
      } catch (e) {
        notifyError('删除失败', '网络异常，请稍后重试')
      }
    },
    /** 是否显示「回复」按钮：不是自己发的评论才可回复 */
    canReply(comment) {
      if (!comment) return false
      const uid = this.currentUserId
      if (uid == null) return true
      return String(comment.userId) !== uid
    },
    /**
     * 回复某条评论：记录 replyTarget，提交时带 parentId。
     *
     * <p>不再往内容里塞「@昵称」前缀——那样后端识别不出回复对象，
     * 被回复的好友也收不到提醒（用户反馈过「回复好友，好友收不到」）。</p>
     */
    replyTo(comment) {
      this.replyTarget = comment
      this.$nextTick(() => {
        const el = this.$refs.commentInput
        if (el && el.focus) el.focus()
      })
    },
    /** 取消回复，回到发顶层评论。 */
    cancelReply() {
      this.replyTarget = null
    },
    async toggleLike() {
      const previous = this.liked
      const previousCount = this.likeCount
      // 乐观更新，失败再回滚
      // 注意：likeCount 可能是后端 Long 转来的字符串，先 toNumber 再算，
      // 否则 "0" + 1 会拼成 "01"（用户反馈过角标显示 01 的问题）
      const current = toNumber(this.likeCount)
      this.liked = !this.liked
      this.likeCount = this.liked ? current + 1 : Math.max(current - 1, 0)
      try {
        const res = this.liked
          ? await like({ resourceType: this.resourceType, resourceId: this.resourceId })
          : await unlike(this.resourceType, this.resourceId)
        if (!res.success || res.code !== 200) {
          this.liked = previous
          this.likeCount = previousCount
          notifyError('操作失败', res.message || '点赞状态未能保存，请稍后重试')
          return
        }
        this.$emit('changed')
        // 点赞可能达成「第一次点赞 / 累计点赞」类成就
        runAchievementCheck(true)
      } catch (e) {
        this.liked = previous
        this.likeCount = previousCount
        notifyError('操作失败', '网络异常，请稍后重试')
      }
    },
    formatTime(value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d.getTime())) return ''
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${m}-${day} ${h}:${min}`
    }
  }
}
</script>

<style scoped>
/* 没有遮罩：气泡是「贴在时间块旁边的浮层」，底下的时间表仍可继续悬停/点击，
   否则一层全屏遮罩会把鼠标和时间块隔开，悬停预览也就无从谈起了。 */
.block-bubble {
  /* 落点由 JS 按时间块的 getBoundingClientRect() 算好后写进 style（viewport 坐标系） */
  position: fixed;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  max-height: min(64vh, 460px);
  background: #fffdf2;
  border: 2px solid #c9d9b5;
  border-radius: 14px;
  box-shadow: 0 14px 32px -12px rgba(38, 59, 41, 0.4), 0 2px 6px rgba(38, 59, 41, 0.08);
  /* 不能 hidden：指回时间块的小箭头画在盒子外面，会被裁掉 */
  overflow: visible;
  font-family: "Microsoft YaHei", sans-serif;
}

/* 指向时间块的小尖角：默认在左边缘（气泡在块右侧），翻转时改画在右边缘。
   纵向位置由 JS 写入 --bubble-arrow（对齐时间块的垂直中心）。 */
.block-bubble::before {
  content: "";
  position: absolute;
  left: -6px;
  top: var(--bubble-arrow, -999px);
  width: 10px;
  height: 10px;
  margin-top: -5px;
  background: inherit;
  border-left: 2px solid #c9d9b5;
  border-bottom: 2px solid #c9d9b5;
  border-top-left-radius: 2px;
  transform: rotate(45deg);
}

.block-bubble.is-placed-left::before {
  left: auto;
  right: -6px;
  border-left: none;
  border-bottom: none;
  border-right: 2px solid #c9d9b5;
  border-top: 2px solid #c9d9b5;
}

.bubble-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 8px 12px;
  border-bottom: 1px solid #e3efe6;
  flex: none;
}
.head-dot {
  width: 4px;
  height: 26px;
  border-radius: 3px;
  flex: none;
  background: #4aa46a;
}
.head-text {
  flex: 1;
  min-width: 0;
}
.head-context {
  margin-bottom: 2px;
  color: #6f8176;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
}
.head-title {
  font-size: 13px;
  font-weight: 700;
  color: #263b29;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.head-sub {
  font-size: 11px;
  color: #8a9a78;
  margin-top: 1px;
}

.icon-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #a8b79a;
  border-radius: 8px;
  transition: color 0.15s ease, background 0.15s ease;
}
.icon-btn:hover {
  background: #eef4e4;
  color: #4aa46a;
}

.like-btn {
  gap: 3px;
  padding: 3px 6px;
  flex: none;
}
.like-btn.liked {
  color: #e0574f;
}
.like-btn.liked:hover {
  background: #fdeceb;
  color: #e0574f;
}
.like-count {
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.close-btn {
  width: 22px;
  height: 22px;
  flex: none;
}

.bubble-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  scrollbar-width: thin;
}
.bubble-body::-webkit-scrollbar {
  width: 5px;
}
.bubble-body::-webkit-scrollbar-thumb {
  background: #d5e2cd;
  border-radius: 3px;
}

.tip {
  color: #8a9a78;
  font-size: 12px;
  text-align: center;
  padding: 12px 0;
}
.tip.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #a3b294;
}

.comment-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.comment-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #b8d49b;
  border: 1.5px solid #57795c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  flex: none;
}
.comment-body {
  flex: 1;
  min-width: 0;
}
.comment-meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.comment-nick {
  font-size: 12px;
  font-weight: 700;
  color: #263b29;
}
.comment-time {
  font-size: 10px;
  color: #a8b79a;
}
.comment-text {
  font-size: 12px;
  line-height: 1.45;
  color: #3f5342;
  margin-top: 2px;
  word-break: break-word;
  white-space: pre-wrap;
}
.del-btn {
  width: 20px;
  height: 20px;
  flex: none;
}
.del-btn:hover {
  background: #fdecec;
  color: #d9534f;
}
.reply-btn {
  width: 20px;
  height: 20px;
  flex: none;
}
.reply-btn:hover {
  background: #eef4e4;
  color: #4aa46a;
}

.bubble-foot {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 10px 12px;
  border-top: 1px solid #e3efe6;
  flex: none;
}
.comment-input {
  flex: 1;
  min-width: 0;
  border: 1px solid #e3efe6;
  background: #f7fbf8;
  border-radius: 10px;
  padding: 7px 10px;
  font-size: 12px;
  color: #263b29;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.comment-input:focus {
  border-color: #a5cdb3;
  box-shadow: 0 0 0 3px rgba(145, 166, 123, 0.2);
}
.comment-input::placeholder {
  color: #b3c0a6;
}
.send-btn {
  width: 30px;
  height: 30px;
  flex: none;
  border: 2px solid #987a2c;
  background: #f1dc7b;
  color: #263b29;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.send-btn:hover:not(:disabled) {
  background: #e8d06a;
}
.send-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.bubble-fade-enter-active,
.bubble-fade-leave-active {
  transition: opacity 0.16s ease;
}
.bubble-fade-enter,
.bubble-fade-leave-to {
  opacity: 0;
}
.bubble-fade-enter-active .block-bubble,
.bubble-fade-leave-active .block-bubble {
  transition: transform 0.16s ease;
}
/* 从时间块那一侧「冒」出来：只做轻微缩放，不做位移，避免落点被动画带偏 */
.bubble-fade-enter .block-bubble,
.bubble-fade-leave-to .block-bubble {
  transform: scale(0.97);
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   评论面板固定在视口中心，遮罩拦截底下时间块的触碰，避免误切换目标。 */
/* 面板外壳（白底 + 极淡中性描边 + 柔阴影） */
.block-bubble{
  background:var(--lp-surface,#fff);
  border:1px solid var(--lp-border,#e3efe6);      /* 2026-10-04：去掉 3px 绿系描边，改极淡中性描边 */
  border-radius:1rem;
  box-shadow:var(--lp-shadow-lg,0 8px 24px rgba(52,102,68,.08));
}
/* 小尖角跟着外壳换色换粗细（底色由 background:inherit 自动跟随） */
.block-bubble::before,
.block-bubble.is-placed-left::before{
  border-color:var(--lp-border,#e3efe6);
  border-width:1px;
}
/* 头部 */
.bubble-head{
  border-bottom:1px solid var(--lp-border-soft,#eef5f0);
}
.head-dot{
  background:var(--lp-primary,#4aa46a);
}
.head-title{
  color:var(--lp-text,#26332b);
}
.head-sub{
  color:var(--lp-text-3,#94a59b);
}
/* 图标按钮（点赞 / 关闭 / 删除） */
.icon-btn{
  border-radius:0.5rem;
  color:var(--lp-text-3,#94a59b);
}
.icon-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.like-btn.liked{
  color:#d9534f;
}
.like-btn.liked:hover{
  background:#fdecec;
  color:#d9534f;
}
.del-btn:hover{
  background:#fdecec;
  color:#d9534f;
}
.reply-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
/* 滚动条 */
.bubble-body::-webkit-scrollbar-thumb{
  background:var(--lp-primary-soft2,#d5ebdb);
}
/* 评论列表 */
.tip{
  color:var(--lp-text-3,#94a59b);
}
.tip.empty{
  color:var(--lp-text-3,#94a59b);
}
.comment-avatar{
  background:var(--lp-primary,#4aa46a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
.comment-nick{
  color:var(--lp-text,#26332b);
}
.comment-time{
  color:var(--lp-text-3,#94a59b);
}
.comment-text{
  color:var(--lp-text,#26332b);
}
/* 输入区 */
.bubble-foot{
  border-top:1px solid var(--lp-border-soft,#eef5f0);
}
.comment-input{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
}
.comment-input:focus{
  border-color:var(--lp-primary,#4aa46a);
  box-shadow:none;
}
.comment-input::placeholder{
  color:var(--lp-text-3,#94a59b);
}
/* 发送主按钮（实心） */
.send-btn{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary,#4aa46a);
  border-radius:0.5rem;
  color:#fff;
}
.send-btn:hover:not(:disabled){
  background:var(--lp-primary-dark,#2f7a4a);
}

/* ===== 2026-10-04：去掉评论气泡中间的两条横向分割线 =====
   原本「头部 border-bottom + 输入区 border-top」把评论列表夹在两条线之间，
   视觉上很碎。改为不画线，靠头部/输入区自身的留白与输入框描边做区隔，面板更简洁大气。 */
.bubble-head{
  border-bottom:none;
}
.bubble-foot{
  border-top:none;
}

/* ===== 楼中楼：回复缩进 + 回复目标提示条 ===== */
/* 回复行缩进，并在左侧画一条细引导线 */
.comment-item.is-reply {
  margin-left: 18px;
  padding-left: 10px;
  border-left: 2px solid var(--lp-primary-soft2, #d5ebdb);
}
.comment-item.is-reply .comment-avatar {
  width: 20px;
  height: 20px;
  font-size: 10px;
}
/* 「回复 @昵称：」内联前缀 */
.reply-to {
  color: var(--lp-primary-dark, #2f7a4a);
  font-weight: 600;
}
/* 输入区改为纵向：回复提示条在上，输入行在下 */
.bubble-foot {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.foot-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.reply-target {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 4px 8px;
  font-size: 11px;
  color: var(--lp-primary-dark, #2f7a4a);
  background: var(--lp-primary-soft, #e9f5ec);
  border-radius: 0.5rem;
}
.reply-target-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.reply-cancel {
  width: 18px;
  height: 18px;
  flex: none;
}
</style>
